/**
 * `/org/:orgSlug/requests`, `/org/:orgSlug/requests/activity/:deliveryId` and
 * `/org/:orgSlug/feedback/:requestId`.
 *
 * Three URLs, one surface. The second and third select a row inside the list
 * rather than opening a page of their own: a feedback link is the one an
 * author hands a recipient, and a mention or reply opens the document it
 * points at beside the list, so someone who arrived for one thing sees the
 * others waiting on them.
 */

import { useMemo, useState } from 'react';
import { Outlet, useOutletContext, useParams } from 'react-router-dom';

import { RequestActivityPane, RequestActivityUnavailable } from '../requests/RequestActivityPane';
import { useRequestsInbox } from '../requests/RequestsInboxContext';
import { RequestsShell, RequestsUnselected } from '../requests/RequestsShell';
import { selectRequestRows, type RequestsFilterId } from '../requests/requestsInboxModel';
import type { OrganizationRouteContext } from './workspaceContext';

export function RequestsWorkspaceRoute() {
  const context = useOutletContext<OrganizationRouteContext>();
  const { requestId, deliveryId } = useParams<{ requestId?: string; deliveryId?: string }>();
  // Defaults to everything undismissed rather than to unread. Reading a request
  // consumes its delivery, so an unread-only default would make the row the
  // reader is looking at disappear from under them.
  const [filter, setFilter] = useState<RequestsFilterId>('all');
  return (
    <RequestsShell
      team={context.team}
      getTeamJwt={context.getTeamJwt}
      filter={filter}
      onFilterChange={setFilter}
      selectedRequestId={requestId}
      selectedDeliveryId={deliveryId}
    >
      <Outlet context={context} />
    </RequestsShell>
  );
}

export function RequestsIndexRoute() {
  const { team } = useOutletContext<OrganizationRouteContext>();
  return <RequestsUnselected orgName={team.name} />;
}

/**
 * The document behind one inbox row, resolved from the live inbox rather than
 * fetched: the row is the reader's context, and a delivery that has been
 * dismissed since the link was made has nothing to show.
 */
export function RequestActivityRoute() {
  const { team, teamSession, getTeamJwt } = useOutletContext<OrganizationRouteContext>();
  const { deliveryId } = useParams<{ deliveryId: string }>();
  const { snapshot } = useRequestsInbox();
  // Over the unfiltered set: the active chip may be hiding the row the link names.
  const row = useMemo(
    () => (deliveryId ? selectRequestRows(snapshot.deliveries).rows.find((candidate) => candidate.deliveryId === deliveryId) ?? null : null),
    [deliveryId, snapshot.deliveries],
  );
  if (!row) {
    return snapshot.status === 'loading'
      ? null
      : (
        <RequestActivityUnavailable
          orgName={team.name}
          title="This activity is no longer in your inbox"
          detail="It was dismissed, or the link points at someone else's inbox."
        />
      );
  }
  return <RequestActivityPane team={team} teamSession={teamSession} getTeamJwt={getTeamJwt} row={row} />;
}
